import {
  POLICY_DECISIONS,
  type PolicyDecision,
  TOOL_EFFECTS,
  type ToolEffect,
  type ToolPolicyRule,
} from '@af/shared';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Empty } from '@/components/ui/misc';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { cn } from '@/lib/utils';

const DECISION_CLS: Record<PolicyDecision, string> = {
  allow: 'text-success',
  ask: 'text-warn',
  deny: 'text-danger',
};

/** Glob → RegExp, `*` matches any chars incl. dots (tool names are flat), `?` one char. */
export function globMatch(pattern: string, name: string): boolean {
  const re = new RegExp(
    `^${pattern
      .replace(/[.+^${}()|[\]\\]/g, '\\$&')
      .replace(/\*/g, '.*')
      .replace(/\?/g, '.')}$`,
  );
  return re.test(name);
}

export function PolicyEditor({
  rules,
  onChange,
  disabled,
}: {
  rules: ToolPolicyRule[];
  onChange: (r: ToolPolicyRule[]) => void;
  disabled?: boolean;
}) {
  const update = (i: number, patch: Partial<ToolPolicyRule>) =>
    onChange(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="flex flex-col gap-2">
      {rules.length === 0 ? (
        <Empty>No rules — effect defaults apply (read = allow, everything else = ask).</Empty>
      ) : (
        <Table>
          <THead>
            <tr>
              <TH>Tool pattern (glob)</TH>
              <TH>Effect</TH>
              <TH>Decision</TH>
              <TH className="w-20">Priority</TH>
              <TH className="w-8">
                <span className="sr-only">Remove</span>
              </TH>
            </tr>
          </THead>
          <TBody>
            {rules.map((r, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: rules are positional
              <TR key={i}>
                <TD>
                  <Input
                    aria-label="Tool pattern"
                    value={r.toolPattern}
                    disabled={disabled}
                    onChange={(e) => update(i, { toolPattern: e.target.value })}
                    placeholder="mcp.github.*"
                  />
                </TD>
                <TD>
                  <Select
                    aria-label="Effect"
                    value={r.effect ?? ''}
                    disabled={disabled}
                    onChange={(e) =>
                      update(i, { effect: (e.target.value || undefined) as ToolEffect | undefined })
                    }
                  >
                    <option value="">any</option>
                    {TOOL_EFFECTS.map((e) => (
                      <option key={e} value={e}>
                        {e}
                      </option>
                    ))}
                  </Select>
                </TD>
                <TD>
                  <Select
                    aria-label="Decision"
                    value={r.decision}
                    disabled={disabled}
                    className={cn('font-bold', DECISION_CLS[r.decision])}
                    onChange={(e) => update(i, { decision: e.target.value as PolicyDecision })}
                  >
                    {POLICY_DECISIONS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </Select>
                </TD>
                <TD>
                  <Input
                    aria-label="Priority"
                    type="number"
                    value={r.priority}
                    disabled={disabled}
                    onChange={(e) => update(i, { priority: Number(e.target.value) || 0 })}
                  />
                </TD>
                <TD>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove rule ${r.toolPattern}`}
                    disabled={disabled}
                    onClick={() => onChange(rules.filter((_, j) => j !== i))}
                  >
                    ✕
                  </Button>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
      {!disabled ? (
        <div>
          <Button
            size="sm"
            onClick={() => onChange([...rules, { toolPattern: '', decision: 'ask', priority: 0 }])}
          >
            + Add rule
          </Button>
        </div>
      ) : null}
      <p className="text-2xs text-muted">
        Resolution: exact agent rule → agent glob → org rule → effect default.{' '}
        <span className="text-danger">deny</span> removes the tool; <span className="text-warn">ask</span>{' '}
        pauses the run for approval.
      </p>
    </div>
  );
}
